import { useMemo } from "react";
import { Controller, useForm, useFormState } from "react-hook-form";

import { AccountDetailsMin } from "../../../services";
import {
  ACCOUNT_TYPES,
  ACCOUNT_TYPE_OPTIONS,
  accountTypeOptionsFor,
  createDefaultAccount,
  FORM_ID,
} from "./constants";
import { AccountPanelProps } from "./types";
import {
  Field,
  FieldHelper,
  FieldLabel,
  FormCard,
  FormPanel,
  LabelRow,
  TwoColumnRow,
  discardCopyOf,
} from "../../../components/FormPanel";
import {
  FormInput,
  FormMoneyInput,
  InputDate,
} from "../../../components/Input";
import {
  buildDraftDetails,
  ConfirmDetail,
  formatOptionalCurrency,
  joinDetailParts,
} from "../../../components/PopUp/confirmShared";
import { CashbackSettingsFields } from "../../../components/CashbackSettingsFields";
import { DateFormat, formatDate, isAfterLocalToday } from "../../../utils";
import {
  cashbackAccountTypes,
  cashbackFormValues,
} from "../../../utils/cashback";
import { SegmentedControl } from "../../../components/MobileForm";
import { MobileAccountForm } from "./MobileAccountForm";
import { accountTypeToStringMap } from "../types";

export const AccountPanel = ({
  account,
  isOpen,
  onClose,
  handleOnSubmit,
}: AccountPanelProps) => {
  const accountDefault = useMemo(() => {
    if (!account) {
      return undefined;
    }
    const accountType = ACCOUNT_TYPES.find(
      (type) => type.value === accountTypeToStringMap[account.accountType],
    ) ?? {
      value: accountTypeToStringMap[account.accountType],
      label: accountTypeToStringMap[account.accountType],
    };
    return {
      ...account,
      ...cashbackFormValues(account),
      accountType,
      initialBalance: String(account.initialBalance),
    };
  }, [account]);

  const resetValues = accountDefault ?? createDefaultAccount();

  const form = useForm<AccountDetailsMin>({
    defaultValues: resetValues,
    mode: "onBlur",
    reValidateMode: "onChange",
  });
  const { watch, control, getValues } = form;
  const { isSubmitting } = useFormState({ control });

  const watchedName = watch("name");

  const validationRules = useMemo(
    () => ({
      name: {
        required: "Name is required",
        minLength: { value: 3, message: "Name must be at least 3 characters" },
        maxLength: { value: 100, message: "Name cannot exceed 100 characters" },
      },
      initialBalance: {
        required: "Initial Balance is required",
      },
      institution: {
        minLength: {
          value: 3,
          message: "Institution must be at least 3 characters",
        },
        maxLength: {
          value: 100,
          message: "Institution cannot exceed 100 characters",
        },
      },
      accountType: {
        required: "Account Type is required",
      },
      startDate: {
        required: "Date is required",
        validate: {
          notInFuture: (value: string) =>
            isAfterLocalToday(value) ? "Date cannot be in the future" : true,
        },
      },
    }),
    [],
  );

  const isEdit = Boolean(account);
  // The type is fixed at creation: it decides which accounting model owns the account, so the API
  // rejects a change rather than leaving a row no read path can classify.
  const isTypeDisabled = isSubmitting || isEdit;
  const eyebrow = isEdit ? "EDIT ACCOUNT" : "CREATE ACCOUNT";
  const panelTitle = watchedName || "";
  const mobileTitle = isEdit ? "Edit account" : "Create account";

  const buildDiscardDetails = (): ConfirmDetail => {
    const accountValues = getValues();
    const formattedBalance = formatOptionalCurrency(
      accountValues.initialBalance,
    );

    if (isEdit) {
      return {
        pill: {
          label: (accountValues.accountType?.label ?? "ACCOUNT").toUpperCase(),
          variant: "DEFAULT",
        },
        title: accountValues.name || "Account",
        subtitle: joinDetailParts(
          accountValues.institution,
          formatDate(accountValues.startDate, DateFormat.DMY_SPACE),
        ),
        amount: formattedBalance,
        amountVariant: "neutral",
      };
    }

    return buildDraftDetails({
      noun: "account",
      fieldValues: [
        accountValues.name,
        accountValues.accountType,
        accountValues.institution,
        accountValues.initialBalance,
        accountValues.startDate,
      ],
      amount: formattedBalance,
      primaryLabel: accountValues.name,
    });
  };

  const discard = {
    ...discardCopyOf(isEdit, "account"),
    details: buildDiscardDetails,
  };

  const accountTypeOptions = accountTypeOptionsFor(accountDefault?.accountType);
  const shouldShowCashback = cashbackAccountTypes.some(
    (type) => type === watch("accountType")?.value,
  );

  return (
    <FormPanel
      open={isOpen}
      onClose={onClose}
      ariaLabel="Account form"
      eyebrow={eyebrow}
      title={panelTitle}
      form={form}
      resetValues={resetValues}
      formId={FORM_ID}
      onSubmit={handleOnSubmit}
      submitLabel="Save changes"
      discard={discard}
      mobile={{
        title: mobileTitle,
        submitLabel: "Save",
        fields: (
          <MobileAccountForm
            control={control}
            validationRules={validationRules}
            isSubmitting={isSubmitting}
            isTypeDisabled={isTypeDisabled}
            isEdit={isEdit}
          />
        ),
      }}
    >
      <FormCard>
        <Field>
          <LabelRow>
            <FieldLabel htmlFor="account-name">Account name *</FieldLabel>
          </LabelRow>
          <FormInput
            id="account-name"
            required
            rules={validationRules.name}
            name="name"
            control={control}
            handleClear={true}
            disabled={isSubmitting}
            placeholder="Account Name"
            autoFocus={true}
          />
        </Field>

        <Field>
          <LabelRow>
            <FieldLabel>Type *</FieldLabel>
          </LabelRow>
          <Controller
            name="accountType"
            control={control}
            rules={validationRules.accountType}
            render={({ field }) => (
              <SegmentedControl
                options={accountTypeOptions}
                selectedValue={field.value?.value ?? ""}
                disabled={isTypeDisabled}
                onChange={(value) => {
                  const option = ACCOUNT_TYPE_OPTIONS.find(
                    (type) => type.value === value,
                  );
                  field.onChange(option);
                }}
              />
            )}
          />
          {isEdit && (
            <FieldHelper>
              The type is set when the account is created and can't be changed.
            </FieldHelper>
          )}
        </Field>
      </FormCard>

      <FormCard>
        <Field>
          <LabelRow>
            <FieldLabel htmlFor="account-institution">Institution</FieldLabel>
          </LabelRow>
          <FormInput
            id="account-institution"
            rules={validationRules.institution}
            name="institution"
            control={control}
            handleClear={true}
            disabled={isSubmitting}
            placeholder="Institution Name"
          />
        </Field>

        <TwoColumnRow>
          <Field>
            <LabelRow>
              <FieldLabel htmlFor="account-initial-balance">
                Initial balance *
              </FieldLabel>
            </LabelRow>
            <FormMoneyInput
              id="account-initial-balance"
              required
              rules={validationRules.initialBalance}
              name="initialBalance"
              control={control}
              handleClear={true}
              disabled={isSubmitting}
              placeholder={"Enter starting balance"}
            />
          </Field>
          <Field>
            <LabelRow>
              <FieldLabel htmlFor="account-created-on">Created on *</FieldLabel>
            </LabelRow>
            <InputDate
              id="account-created-on"
              rules={validationRules.startDate}
              name="startDate"
              control={control}
              disabled={isSubmitting}
              maxDate={new Date()}
              placeholder="YYYY/MM/DD"
            />
          </Field>
        </TwoColumnRow>
        <FieldHelper>
          Recalculates the current balance. Transactions are not affected.
        </FieldHelper>
      </FormCard>
      {shouldShowCashback && (
        <CashbackSettingsFields control={control} disabled={isSubmitting} />
      )}
    </FormPanel>
  );
};
